import React, {useEffect, useMemo, useState} from "react";
import * as countryCodesList from "country-codes-list";
import {getUserAPI} from "../../network/loggedInCalls/GETUser";
import {triggerCheqmAPI} from "../../network/loggedInCalls/PATCHTriggerCheqm";
import {getCustomersAPI} from "../../network/loggedInCalls/GETCustomers";
import {postalCodeLookupAPI} from "../../network/publicCalls/GETPostalCodeLookup";

const EMPTY_ADDRESS = {
    zip_code: "",
    house_number: "",
    house_number_extension: "",
    street_name: "",
    city: "",
    country: "NL",
};

// NL and BE first: CheqM only knows Dutch and Belgian addresses.
const COUNTRY_OPTIONS = (() => {
    // The list holds some codes twice (e.g. IO), keep the first one so option keys stay unique.
    const byCode = {};
    countryCodesList.all().forEach((country) => {
        const code = String(country.countryCode || "").toUpperCase();
        if (code && !byCode[code]) byCode[code] = {code, name: String(country.countryNameEn || "")};
    });
    const all = Object.values(byCode).sort((a, b) => a.name.localeCompare(b.name));
    const first = ["NL", "BE"];
    return [
        ...first.map((code) => all.find((country) => country.code === code)).filter(Boolean),
        ...all.filter((country) => !first.includes(country.code)),
    ];
})();

const normalizeZipCode = (value) => String(value || "").replace(/\s+/g, "").toUpperCase();
const isValidDutchPostcode = (value) => /^[1-9][0-9]{3}[A-Z]{2}$/.test(normalizeZipCode(value));

const isCompleteAddress = (address) => Boolean(
    address.zip_code && address.house_number && address.street_name && address.city && address.country &&
    (address.country !== "NL" || isValidDutchPostcode(address.zip_code))
);

const formatAddress = (address) => [
    [address.street_name, [address.house_number, address.house_number_extension].filter(Boolean).join(" ")].filter(Boolean).join(" "),
    [address.zip_code, address.city].filter(Boolean).join(" "),
    address.country,
].filter(Boolean).join(", ");

/**
 * CheqM runs on the residence address, so the customer confirms or replaces it first, like the app's CheqmScreening.
 * onDone receives the reloaded customers (or null) once CheqM has been triggered.
 */
export default function CheqmAddressModal({authToken, subsidiaryId, onDone, onClose}) {
    const [step, setStep] = useState("loading");
    const [address, setAddress] = useState(EMPTY_ADDRESS);
    const [showErrors, setShowErrors] = useState(false);
    const [isLookingUp, setIsLookingUp] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getUserAPI({authToken, subsidiaryId})
            .then((user) => {
                if (cancelled) return;
                const residence = {...EMPTY_ADDRESS, ...(user?.residence_address || {})};
                if (!residence.country) residence.country = "NL";
                setAddress(residence);
                setStep(isCompleteAddress(residence) ? "confirm" : "edit");
            })
            .catch(() => !cancelled && setStep("edit"));
        return () => {
            cancelled = true;
        };
    }, [authToken, subsidiaryId]);

    const setField = (field, value) => setAddress((prev) => ({...prev, [field]: value}));

    const lookup = async () => {
        if (address.country !== "NL" || !isValidDutchPostcode(address.zip_code) || !address.house_number) return;
        setIsLookingUp(true);
        try {
            const response = await postalCodeLookupAPI({zipCode: address.zip_code, houseNumber: address.house_number});
            setAddress((prev) => ({
                ...prev,
                street_name: response?.street || prev.street_name,
                city: response?.city || prev.city,
            }));
        } catch (error) {
            // Lookup is a convenience; the customer can still type the street and city.
        } finally {
            setIsLookingUp(false);
        }
    };

    const submit = async () => {
        if (!isCompleteAddress(address)) {
            setShowErrors(true);
            setStep("edit");
            return;
        }

        setStep("submitting");
        let customers = null;
        try {
            await triggerCheqmAPI({
                authToken,
                subsidiaryId,
                residenceAddress: {...address, zip_code: normalizeZipCode(address.zip_code)},
            });
        } catch (error) {
            // The outcome is read from the customer below either way.
        }
        try {
            customers = await getCustomersAPI({authToken, subsidiaryId});
        } catch (error) {
            customers = null;
        }
        onDone(customers);
    };

    const fieldClass = (invalid) => `kav-input-field__input${showErrors && invalid ? " is-invalid" : ""}`;
    const countryOptions = useMemo(() => COUNTRY_OPTIONS, []);

    return (
        <div className="kav-simple-modal" role="dialog" aria-modal="true" aria-labelledby="cheqm-address-title">
            <button
                type="button"
                className="kav-simple-modal__backdrop"
                onClick={() => step !== "submitting" && onClose()}
                aria-label="Sluiten"
            />
            <div className="kav-simple-modal__card kav-cheqm-modal">
                <h3 id="cheqm-address-title">Controleer je woonadres</h3>
                <p>
                    We moeten de gegevens die je tijdens de registratie hebt opgegeven opnieuw verifiëren.
                    Klopt je woonadres nog?
                </p>

                {(step === "loading" || step === "submitting") && (
                    <div className="kav-callout kav-auth-status">
                        <span className="kav-spinner" aria-hidden="true"></span>
                        <span>{step === "loading" ? "Gegevens laden..." : "Je gegevens worden gecontroleerd..."}</span>
                    </div>
                )}

                {step === "confirm" && (
                    <>
                        <p><strong>{formatAddress(address)}</strong></p>
                        <div className="kav-auth-actions kav-auth-actions--end">
                            <button type="button" className="kav-step-actions__btn kav-step-actions__btn--ghost" onClick={() => setStep("edit")}>
                                Nee, wijzigen
                            </button>
                            <button type="button" className="kav-step-actions__btn kav-step-actions__btn--primary" onClick={submit}>
                                Ja, dit klopt
                            </button>
                        </div>
                    </>
                )}

                {step === "edit" && (
                    <>
                        <div className="kav-input-field">
                            <label className="kav-input-field__label" htmlFor="cheqm-country">Land</label>
                            <select
                                id="cheqm-country"
                                className="kav-input-field__input"
                                value={address.country}
                                onChange={(event) => setField("country", event.target.value)}
                            >
                                {countryOptions.map(({code, name}) => <option key={code} value={code}>{name}</option>)}
                            </select>
                        </div>
                        <div className="kav-register-mobile__grid2">
                            <div className="kav-input-field">
                                <label className="kav-input-field__label" htmlFor="cheqm-zip">Postcode</label>
                                <input
                                    id="cheqm-zip"
                                    className={fieldClass(!address.zip_code || (address.country === "NL" && !isValidDutchPostcode(address.zip_code)))}
                                    value={address.zip_code}
                                    onChange={(event) => setField("zip_code", normalizeZipCode(event.target.value))}
                                    onBlur={lookup}
                                    placeholder="1234AB"
                                />
                            </div>
                            <div className="kav-input-field">
                                <label className="kav-input-field__label" htmlFor="cheqm-house">Huisnummer</label>
                                <input
                                    id="cheqm-house"
                                    className={fieldClass(!address.house_number)}
                                    value={address.house_number}
                                    onChange={(event) => setField("house_number", event.target.value.replace(/[^\d]/g, ""))}
                                    onBlur={lookup}
                                    placeholder="10"
                                />
                            </div>
                        </div>
                        <div className="kav-input-field">
                            <label className="kav-input-field__label" htmlFor="cheqm-addition">Toevoeging</label>
                            <input
                                id="cheqm-addition"
                                className="kav-input-field__input"
                                value={address.house_number_extension || ""}
                                onChange={(event) => setField("house_number_extension", event.target.value)}
                                placeholder="A"
                            />
                        </div>
                        <div className="kav-input-field">
                            <label className="kav-input-field__label" htmlFor="cheqm-street">Straat</label>
                            <input
                                id="cheqm-street"
                                className={fieldClass(!address.street_name)}
                                value={address.street_name}
                                onChange={(event) => setField("street_name", event.target.value)}
                                placeholder="Straatnaam"
                                disabled={isLookingUp}
                            />
                        </div>
                        <div className="kav-input-field">
                            <label className="kav-input-field__label" htmlFor="cheqm-city">Plaats</label>
                            <input
                                id="cheqm-city"
                                className={fieldClass(!address.city)}
                                value={address.city}
                                onChange={(event) => setField("city", event.target.value)}
                                placeholder="Plaats"
                                disabled={isLookingUp}
                            />
                        </div>
                        {showErrors && !isCompleteAddress(address) && (
                            <p className="kav-callout">Vul je volledige woonadres in.</p>
                        )}
                        <div className="kav-auth-actions kav-auth-actions--end">
                            <button type="button" className="kav-step-actions__btn kav-step-actions__btn--ghost" onClick={onClose}>
                                Annuleren
                            </button>
                            <button type="button" className="kav-step-actions__btn kav-step-actions__btn--primary" onClick={submit}>
                                Doorgaan
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
